import React, { useState, useEffect, useMemo } from 'react';
import {
  MapContainer,
  TileLayer,
  CircleMarker,
  Tooltip,
} from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import toast from 'react-hot-toast';
import { devicesAPI, companiesAPI, depotsAPI } from '../services/api';
import { useAuthStore } from '../store/authStore';
import './MapWidget.css';

// Country coordinates (centroid for each country) - comprehensive global list
const COUNTRY_COORDS = {
  // Americas
  USA: [37.0902, -95.7129],
  'United States': [37.0902, -95.7129],
  Canada: [56.1304, -106.3468],
  Mexico: [23.6345, -102.5528],
  Brazil: [-14.2350, -51.9253],
  Argentina: [-38.4161, -63.6167],
  Chile: [-35.6751, -71.5430],
  Peru: [-9.1900, -75.0152],
  Colombia: [4.5709, -74.2973],
  Ecuador: [-1.8312, -78.1834],
  Venezuela: [6.4238, -66.5897],
  Guyana: [4.8604, -58.9302],
  Suriname: [3.9193, -56.0278],
  'French Guiana': [3.9339, -53.1256],
  Bolivia: [-16.2902, -63.5887],
  Paraguay: [-23.4425, -58.4438],
  Uruguay: [-32.5228, -55.7658],
  'Costa Rica': [9.7489, -83.7534],
  Panama: [8.5380, -80.7821],
  Nicaragua: [12.8654, -85.2072],
  Honduras: [15.2000, -86.2419],
  'El Salvador': [13.7942, -88.8965],
  Guatemala: [15.7835, -90.2308],
  Belize: [17.1899, -88.7979],
  Jamaica: [18.1096, -77.2975],
  'Dominican Republic': [18.7357, -70.1627],
  Haiti: [18.9712, -72.2852],
  Cuba: [21.5218, -77.7812],
  'Puerto Rico': [18.2208, -66.5901],
  
  // Europe
  France: [46.2276, 2.2137],
  Germany: [51.1657, 10.4515],
  Spain: [40.4637, -3.7492],
  Italy: [41.8719, 12.5674],
  Portugal: [39.3999, -8.2245],
  UK: [55.3781, -3.4360],
  'United Kingdom': [55.3781, -3.4360],
  Ireland: [53.4129, -8.2439],
  Poland: [51.9194, 19.1451],
  Netherlands: [52.1326, 5.2913],
  Belgium: [50.5039, 4.4699],
  Switzerland: [46.8182, 8.2275],
  Austria: [47.5162, 14.5501],
  'Czech Republic': [49.8175, 15.4730],
  Hungary: [47.1625, 19.5033],
  Romania: [45.9432, 24.9668],
  Bulgaria: [42.7339, 25.4858],
  Greece: [39.0742, 21.8243],
  Serbia: [44.0165, 21.0059],
  Croatia: [45.1000, 15.2000],
  Slovakia: [48.6690, 19.6990],
  Slovenia: [46.1512, 14.9955],
  'Bosnia and Herzegovina': [43.9159, 17.6791],
  Montenegro: [42.7087, 19.3744],
  Albania: [41.1533, 20.1683],
  'North Macedonia': [41.6086, 21.7453],
  Ukraine: [48.3794, 31.1656],
  Belarus: [53.7098, 27.9534],
  Moldova: [47.4116, 28.3699],
  Russia: [61.5240, 105.3188],
  Norway: [60.4720, 8.4689],
  Sweden: [60.1282, 18.6435],
  Finland: [61.9241, 25.7482],
  Denmark: [56.2639, 9.5018],
  Iceland: [64.9631, -19.0208],
  Malta: [35.9375, 14.3754],
  Cyprus: [34.9249, 33.4299],
  Luxembourg: [49.8153, 6.1296],
  
  // Asia
  China: [35.8617, 104.1954],
  Japan: [36.2048, 138.2529],
  India: [20.5937, 78.9629],
  Indonesia: [-0.7893, 113.9213],
  Thailand: [15.8700, 100.9925],
  Vietnam: [14.0583, 108.2772],
  Philippines: [12.8797, 121.7740],
  Malaysia: [4.2105, 101.6964],
  Singapore: [1.3521, 103.8198],
  'South Korea': [35.9078, 127.7669],
  'North Korea': [40.3399, 127.5101],
  Taiwan: [23.6978, 120.9605],
  Pakistan: [30.8753, 69.3432],
  Bangladesh: [23.6850, 90.3563],
  Myanmar: [21.9162, 95.9560],
  Cambodia: [12.5657, 104.9910],
  Laos: [19.8523, 102.4955],
  Mongolia: [46.8625, 103.8467],
  Kazakhstan: [48.0196, 66.9237],
  Uzbekistan: [41.3775, 64.5853],
  Tajikistan: [38.8610, 71.2761],
  Kyrgyzstan: [41.2044, 74.7661],
  Turkmenistan: [38.9697, 59.5563],
  Afghanistan: [33.9391, 67.2994],
  Iran: [32.4279, 53.6880],
  Iraq: [33.2232, 43.6793],
  'Saudi Arabia': [23.8859, 45.0792],
  'United Arab Emirates': [23.4241, 53.8478],
  Oman: [21.4735, 55.9754],
  Yemen: [15.3694, 48.5164],
  Qatar: [25.3548, 51.1839],
  Bahrain: [26.0667, 50.5577],
  Kuwait: [29.3117, 47.4818],
  Israel: [31.0461, 34.8516],
  Palestine: [31.9454, 35.2338],
  Lebanon: [33.8547, 35.8623],
  Jordan: [30.5852, 36.2384],
  Syria: [34.8021, 38.9968],
  Turkey: [38.9637, 35.2433],
  Greece: [39.0742, 21.8243],
  Armenia: [40.0691, 45.0382],
  Azerbaijan: [40.1431, 47.5769],
  Georgia: [42.3154, 43.3569],
  
  // Africa
  Egypt: [26.8206, 30.8025],
  Nigeria: [9.0820, 8.6753],
  'South Africa': [-30.5595, 22.9375],
  Kenya: [-0.0236, 37.9062],
  Ethiopia: [9.1450, 40.4897],
  Ghana: [7.3697, -5.7432],
  Morocco: [31.7917, -7.0926],
  Algeria: [28.0339, 1.6596],
  Tunisia: [33.8869, 9.5375],
  Sudan: [12.8628, 30.8065],
  Tanzania: [-6.3690, 34.8888],
  Uganda: [1.3733, 32.2903],
  Rwanda: [-1.9536, 29.8739],
  Burundi: [-3.3731, 29.9189],
  Zimbabwe: [-19.0154, 29.1549],
  Botswana: [-22.3285, 24.6849],
  Zambia: [-13.1339, 27.8493],
  Malawi: [-13.2543, 34.3015],
  Mozambique: [-18.6657, 35.3291],
  Angola: [-11.2027, 17.8739],
  Namibia: [-22.9575, 18.4904],
  'Ivory Coast': [7.5400, -5.5471],
  Cameroon: [3.8480, 11.5021],
  Senegal: [14.4974, -14.4524],
  Mali: [17.5707, -3.9962],
  'Burkina Faso': [12.2383, -1.5616],
  'Western Sahara': [24.2155, -13.2038],
  Benin: [9.3077, 2.3158],
  Niger: [17.6078, 8.6753],
  Chad: [15.4730, 18.7322],
  'Central African Republic': [6.6111, 20.9394],
  Gabon: [-0.8037, 11.6045],
  'Republic of the Congo': [-4.0383, 21.7587],
  'Democratic Republic of the Congo': [-4.0383, 21.7587],
  Mauritius: [-20.3484, 57.5522],
  Madagascar: [-18.7669, 46.8691],
  Somalia: [5.1521, 46.1996],
  Djibouti: [11.8254, 42.5903],
  Eritrea: [15.1794, 39.7823],
  Mauritania: [21.0079, -10.9408],
  'Equatorial Guinea': [1.6508, 10.2679],
  'Sao Tome and Principe': [0.6300, 6.5243],
  Seychelles: [-4.6796, 55.4920],
  Comoros: [-11.8750, 43.3333],
  Lesotho: [-29.6100, 28.2336],
  Eswatini: [-26.5225, 31.4659],
  'Sierra Leone': [8.4606, -11.7799],
  'Guinea-Bissau': [11.8037, -15.1804],
  Guinea: [9.9456, -9.6966],
  Liberia: [6.4281, -9.4295],
  'Cape Verde': [16.5388, -23.0418],
  Gambia: [13.4549, -15.3101],
  Liberia: [6.4281, -9.4295],
  
  // Oceania
  Australia: [-25.2744, 133.7751],
  'New Zealand': [-40.9006, 174.8860],
  Fiji: [-17.7134, 178.0650],
  'Papua New Guinea': [-6.3150, 143.9555],
  'Solomon Islands': [-9.6457, 160.1562],
  Vanuatu: [-17.7404, 168.3045],
  'Samoa': [-13.7590, -172.1046],
  Kiribati: [-3.3704, -168.7340],
  'Micronesia': [7.4256, 150.5508],
  'Palau': [7.3150, 134.4807],
  'Marshall Islands': [7.1315, 171.1845],
  Tonga: [-21.1789, -175.1982],
  Nauru: [-0.5228, 166.9315],
  Tuvalu: [-8.5211, 179.1982],
};

// Get country coordinates with fallback to center
function getCountryCoords(country) {
  if (!country) return [0, 0];
  const coords = COUNTRY_COORDS[country];
  return coords || [0, 0];
}

// Get color based on availability ratio (available / total)
function getStatusColor(data) {
  if (!data || data.total === 0) return '#888888';

  const availableRatio = (data.available || 0) / data.total;

  if (availableRatio >= 0.8) return '#10b981'; // Green - mostly available
  if (availableRatio >= 0.5) return '#f59e0b'; // Amber - mixed
  return '#ef4444'; // Red - low availability
}

// Simple country marker (no drill-down navigation)
function formatStatusTooltip(data, hideAwaitingContainer) {
  const awaitingText = hideAwaitingContainer ? '' : ` | Awaiting: ${data.awaitingContainer || 0}`;
  return `Available: ${data.available || 0} | In Use: ${data.inUse || 0}${awaitingText}`;
}

function CountryMarker({ country, data, hideAwaitingContainer = false }) {
  const coords = getCountryCoords(country);
  const color = getStatusColor(data);
  
  return (
    <CircleMarker
      center={coords}
      radius={8}
      fillColor={color}
      color={color}
      weight={2}
      opacity={0.8}
      fillOpacity={0.6}
    >
      <Tooltip>
        <div>
          <strong>{country}</strong>
          <br />
          Total: {data.total || 0}
          <br />
          {formatStatusTooltip(data, hideAwaitingContainer)}
        </div>
      </Tooltip>
    </CircleMarker>
  );
}

// Company-level marker
function CompanyMarker({ company, country, data, hideAwaitingContainer = false }) {
  const coords = getCountryCoords(country);
  // Offset companies slightly for visibility
  const offsetLat = coords[0] + (Math.random() - 0.5) * 5;
  const offsetLng = coords[1] + (Math.random() - 0.5) * 5;
  
  const color = getStatusColor(data);
  
  return (
    <CircleMarker
      center={[offsetLat, offsetLng]}
      radius={8}
      fillColor={color}
      color={color}
      weight={2}
      opacity={0.8}
      fillOpacity={0.6}
    >
      <Tooltip>
        <div>
          <strong>{company}</strong>
          <br />
          Total: {data.total || 0}
          <br />
          {formatStatusTooltip(data, hideAwaitingContainer)}
        </div>
      </Tooltip>
    </CircleMarker>
  );
}

// Depot-level marker
function DepotMarker({ depot, company, country, data, latitude, longitude, hideAwaitingContainer = false }) {
  let coords;
  if (typeof latitude === 'number' && typeof longitude === 'number') {
    coords = [latitude, longitude];
  } else {
    coords = getCountryCoords(country);
  }

  const color = getStatusColor(data);
  
  return (
    <CircleMarker
      center={coords}
      radius={8}
      fillColor={color}
      color={color}
      weight={2}
      opacity={0.8}
      fillOpacity={0.6}
    >
      <Tooltip>
        <div>
          <strong>{depot}</strong>
          <br />
          Total: {data.total || 0}
          <br />
          {formatStatusTooltip(data, hideAwaitingContainer)}
        </div>
      </Tooltip>
    </CircleMarker>
  );
}

// Main MapWidget Component
export default function MapWidget({
  onViewStateChange,
  lockedViewLevel,
  hideCountryFilter = false,
  hideCompanyFilter = false,
  hideDepotLocationColumns = false,
  hideAwaitingContainer = false,
  clientAvailabilityMode = false,
  uploadBatchId = '',
}) {
  const [mapData, setMapData] = useState({});
  const [viewLevel, setViewLevelState] = useState(lockedViewLevel || 'country'); // 'country', 'company', 'depot'
  const [countryFilter, setCountryFilter] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const user = useAuthStore((state) => state.user);
  const role = user?.user_type;

  const operadorAssignedDepotIds = useMemo(() => {
    if (!user) return [];
    const assigned = user.depot_ids || (user.depot_id ? [user.depot_id] : []);
    return (assigned || []).map((id) => Number(id));
  }, [user]);

  // Keep view level in sync if it's locked by parent
  useEffect(() => {
    if (lockedViewLevel) {
      setViewLevelState(lockedViewLevel);
    }
  }, [lockedViewLevel]);

  // Helper to update view level respecting lockedViewLevel
  const setViewLevel = (level) => {
    if (lockedViewLevel) {
      setViewLevelState(lockedViewLevel);
    } else {
      setViewLevelState(level);
    }
  };

  // Load seal data on mount
  useEffect(() => {
    loadMapData();
  }, [uploadBatchId]);

  const loadMapData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch all devices
      // Request devices with optional server-side batch filtering to reduce payload
      const devicesParams = uploadBatchId ? { upload_batch_id: uploadBatchId } : {};
      const [devicesRes, companiesRes, depotsRes] = await Promise.allSettled([
        devicesAPI.getAll(devicesParams),
        companiesAPI.getAll(),
        depotsAPI.getAll(),
      ]);

      let devices = devicesRes.status === 'fulfilled' ? devicesRes.value.data : [];
      let companies = companiesRes.status === 'fulfilled' ? companiesRes.value.data : [];
      let depots = depotsRes.status === 'fulfilled' ? depotsRes.value.data : [];

      if (uploadBatchId) {
        devices = devices.filter((device) => device.upload_batch_id === uploadBatchId);
      }

      // If operador, restrict to assigned depots only
      if (role === 'operador') {
        const assignedSet = new Set(operadorAssignedDepotIds.map((id) => Number(id)));
        devices = devices.filter((d) => assignedSet.has(Number(d.depot_id)));
        depots = depots.filter((d) => assignedSet.has(Number(d.id)));

        // Keep only companies that own the remaining depots/devices
        const allowedCompanyIds = new Set();
        depots.forEach((d) => { if (d.company_id) allowedCompanyIds.add(Number(d.company_id)); });
        devices.forEach((d) => { if (d.company_id) allowedCompanyIds.add(Number(d.company_id)); });
        companies = companies.filter((c) => allowedCompanyIds.has(Number(c.id)));
      }

      // Build hierarchical data structure
      const data = {};

      devices.forEach((device) => {
        const companyData = companies.find((c) => c.id === device.company_id);
        const depotData = depots.find((d) => d.id === device.depot_id);

        const country = companyData?.country || depotData?.country || 'Unknown';
        const companyName = companyData?.name || 'Unknown';
        const depotName = depotData?.name;
        const hasCompany = !!device.company_id;
        const hasDepot = !!device.depot_id;
        const hasContainer = !!device.container_number;

        const isAvailable = clientAvailabilityMode
          ? hasCompany && !hasContainer
          : hasCompany && !hasDepot && !hasContainer;
        const isInUse = clientAvailabilityMode
          ? hasCompany && hasContainer
          : hasCompany && hasDepot && hasContainer;
        const isAwaitingContainer = clientAvailabilityMode
          ? false
          : hasCompany && hasDepot && !hasContainer;

        const participates = isAvailable || isInUse || isAwaitingContainer;

        // Initialize country
        if (!data[country]) {
          data[country] = {
            total: 0,
            available: 0,
            in_use: 0,
            awaiting_container: 0,
            companies: {},
          };
        }

        // Initialize company
        if (companyName && !data[country].companies[companyName]) {
          data[country].companies[companyName] = {
            total: 0,
            available: 0,
            in_use: 0,
            awaiting_container: 0,
            depots: {},
            country,
          };
        }

        // Initialize depot
        if (companyName && depotName && !data[country].companies[companyName].depots[depotName]) {
          data[country].companies[companyName].depots[depotName] = {
            total: 0,
            available: 0,
            in_use: 0,
            awaiting_container: 0,
            country,
            company: companyName,
            latitude: depotData?.latitude ?? null,
            longitude: depotData?.longitude ?? null,
          };
        }

        if (!participates) {
          return;
        }

        // Increment counts only for devices that fit one of the defined states
        data[country].total += 1;
        if (isAvailable) data[country].available += 1;
        if (isInUse) data[country].in_use += 1;
        if (isAwaitingContainer) data[country].awaiting_container += 1;

        if (companyName) {
          data[country].companies[companyName].total += 1;
          if (isAvailable) data[country].companies[companyName].available += 1;
          if (isInUse) data[country].companies[companyName].in_use += 1;
          if (isAwaitingContainer) data[country].companies[companyName].awaiting_container += 1;

          if (depotName) {
            data[country].companies[companyName].depots[depotName].total += 1;
            if (isAvailable) data[country].companies[companyName].depots[depotName].available += 1;
            if (isInUse) data[country].companies[companyName].depots[depotName].in_use += 1;
            if (isAwaitingContainer) data[country].companies[companyName].depots[depotName].awaiting_container += 1;
          }
        }
      });

      // Convert nested objects to arrays
      Object.keys(data).forEach((country) => {
        data[country].companies = Object.entries(data[country].companies).map(
          ([name, companyData]) => ({
            name,
            ...companyData,
            depots: Object.entries(companyData.depots).map(
              ([depotName, depotData]) => ({
                name: depotName,
                ...depotData,
              })
            ),
          })
        );
      });

      setMapData(data);
    } catch (err) {
      console.error('Error loading map data:', err);
      setError('Failed to load map data');
      toast.error('Error loading map data');
    } finally {
      setLoading(false);
    }
  };

  // Flattened lists for details panel
  const countryList = useMemo(() => {
    return Object.entries(mapData).map(([country, data]) => ({
      name: country,
      total: data.total || 0,
      available: data.available || 0,
      inUse: data.in_use || 0,
      awaitingContainer: data.awaiting_container || 0,
      companyCount: (data.companies || []).length,
    })).sort((a, b) => b.total - a.total);
  }, [mapData]);

  const companyList = useMemo(() => {
    const items = [];
    Object.entries(mapData).forEach(([country, data]) => {
      (data.companies || []).forEach((company) => {
        items.push({
          name: company.name,
          country,
          total: company.total || 0,
          available: company.available || 0,
          inUse: company.in_use || 0,
          awaitingContainer: company.awaiting_container || 0,
          depotCount: (company.depots || []).length,
        });
      });
    });
    return items.sort((a, b) => b.total - a.total);
  }, [mapData]);

  const depotList = useMemo(() => {
    const items = [];
    Object.entries(mapData).forEach(([country, data]) => {
      (data.companies || []).forEach((company) => {
        (company.depots || []).forEach((depot) => {
          items.push({
            name: depot.name,
            country,
            company: company.name,
            total: depot.total || 0,
            available: depot.available || 0,
            inUse: depot.in_use || 0,
            awaitingContainer: depot.awaiting_container || 0,
            latitude: depot.latitude ?? null,
            longitude: depot.longitude ?? null,
          });
        });
      });
    });
    return items.sort((a, b) => b.total - a.total);
  }, [mapData]);

  // Expose current view state and aggregates to parent (for dashboards/charts)
  useEffect(() => {
    if (typeof onViewStateChange === 'function') {
      onViewStateChange({
        viewLevel,
        countryFilter,
        companyFilter,
        countryList,
        companyList,
        depotList,
      });
    }
  }, [onViewStateChange, viewLevel, countryFilter, companyFilter, countryList, companyList, depotList]);

  const filteredCompanies = useMemo(() => {
    return companyList.filter((c) => {
      if (countryFilter && c.country !== countryFilter) return false;
      return true;
    });
  }, [companyList, countryFilter]);

  const filteredDepots = useMemo(() => {
    return depotList.filter((d) => {
      if (countryFilter && d.country !== countryFilter) return false;
      if (companyFilter && d.company !== companyFilter) return false;
      return true;
    });
  }, [depotList, countryFilter, companyFilter]);

  const currentMarkers = useMemo(() => {
    if (viewLevel === 'country') {
      return countryList.map((c) => (
        <CountryMarker
          key={c.name}
          country={c.name}
          data={c}
          hideAwaitingContainer={hideAwaitingContainer}
        />
      ));
    }

    if (viewLevel === 'company') {
      return filteredCompanies.map((c) => (
        <CompanyMarker
          key={`${c.country}-${c.name}`}
          country={c.country}
          company={c.name}
          data={c}
          hideAwaitingContainer={hideAwaitingContainer}
        />
      ));
    }

    if (viewLevel === 'depot') {
      return filteredDepots.map((d) => (
        <DepotMarker
          key={`${d.country}-${d.company}-${d.name}`}
          country={d.country}
          company={d.company}
          depot={d.name}
          data={d}
          latitude={d.latitude}
          longitude={d.longitude}
          hideAwaitingContainer={hideAwaitingContainer}
        />
      ));
    }

    return [];
  }, [viewLevel, countryList, filteredCompanies, filteredDepots, hideAwaitingContainer]);

  if (error) {
    return (
      <div className="map-widget-container">
        <div className="map-error">
          <p>{error}</p>
          <button onClick={loadMapData} className="map-retry-btn">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="map-widget-container">
      <div className="map-header">
        <div className="map-controls">
          <h3>Global Seals Overview</h3>
          <div className="map-level-controls">
            {lockedViewLevel ? (
              <span className="map-level-label">
                View: {lockedViewLevel === 'depot' ? 'Depot' : lockedViewLevel === 'company' ? 'Company' : 'Country'}
              </span>
            ) : (
              <>
                <span className="map-level-label">Level:</span>
                <button
                  type="button"
                  className={`map-level-btn ${viewLevel === 'country' ? 'active' : ''}`}
                  onClick={() => {
                    setViewLevel('country');
                    setCountryFilter('');
                    setCompanyFilter('');
                  }}
                >
                  Country
                </button>
                <button
                  type="button"
                  className={`map-level-btn ${viewLevel === 'company' ? 'active' : ''}`}
                  onClick={() => {
                    setViewLevel('company');
                    setCompanyFilter('');
                  }}
                >
                  Company
                </button>
                <button
                  type="button"
                  className={`map-level-btn ${viewLevel === 'depot' ? 'active' : ''}`}
                  onClick={() => setViewLevel('depot')}
                >
                  Depot
                </button>
              </>
            )}

            {!hideCountryFilter && (viewLevel === 'company' || viewLevel === 'depot') && (
              <select
                className="map-filter-select"
                value={countryFilter}
                onChange={(e) => {
                  setCountryFilter(e.target.value);
                  setCompanyFilter('');
                }}
              >
                <option value="">All Countries</option>
                {countryList.map((c) => (
                  <option key={c.name} value={c.name}>{c.name}</option>
                ))}
              </select>
            )}

            {!hideCompanyFilter && viewLevel === 'depot' && (
              <select
                className="map-filter-select"
                value={companyFilter}
                onChange={(e) => setCompanyFilter(e.target.value)}
              >
                <option value="">All Companies</option>
                {filteredCompanies.map((c) => (
                  <option key={`${c.country}-${c.name}`} value={c.company || c.name}>
                    {c.name} ({c.country})
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>
        <div className="map-legend">
          <div className="legend-item">
            <span className="legend-color" style={{ backgroundColor: '#10b981' }}></span>
            <span>80%+ Available</span>
          </div>
          <div className="legend-item">
            <span className="legend-color" style={{ backgroundColor: '#f59e0b' }}></span>
            <span>50-79% Available</span>
          </div>
          <div className="legend-item">
            <span className="legend-color" style={{ backgroundColor: '#ef4444' }}></span>
            <span>&lt;50% Available</span>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="map-loading">
          <p>Loading map data...</p>
        </div>
      ) : Object.keys(mapData).length === 0 ? (
        <div className="map-empty">
          <p>No seal data available</p>
        </div>
      ) : (
        <div className="map-body">
          <div className="map-body-left">
            <MapContainer center={[0, 0]} zoom={2} className="map-container">
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              />
              {currentMarkers}
            </MapContainer>
          </div>
          <div className="map-body-right">
            {viewLevel === 'country' && (
              <div className="map-details">
                <h4>Countries Overview</h4>
                <div className="map-details-header">
                  <span>Name</span>
                  <span>Total</span>
                  <span>Available</span>
                  <span>In Use</span>
                  {!hideAwaitingContainer && <span>Awaiting Container</span>}
                  <span>Companies</span>
                </div>
                <div className="map-details-rows">
                  {countryList.map((c) => (
                    <div key={c.name} className="map-details-row">
                      <span>{c.name}</span>
                      <span>{c.total}</span>
                      <span>{c.available}</span>
                      <span>{c.inUse}</span>
                      {!hideAwaitingContainer && <span>{c.awaitingContainer}</span>}
                      <span>{c.companyCount}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {viewLevel === 'company' && (
              <div className="map-details">
                <h4>Companies Overview{countryFilter ? ` in ${countryFilter}` : ''}</h4>
                <div className="map-details-header">
                  <span>Company</span>
                  <span>Country</span>
                  <span>Total</span>
                  <span>Available</span>
                  <span>In Use</span>
                  {!hideAwaitingContainer && <span>Awaiting Container</span>}
                  <span>Depots</span>
                </div>
                <div className="map-details-rows">
                  {filteredCompanies.map((c) => (
                    <div key={`${c.country}-${c.name}`} className="map-details-row">
                      <span>{c.name}</span>
                      <span>{c.country}</span>
                      <span>{c.total}</span>
                      <span>{c.available}</span>
                      <span>{c.inUse}</span>
                      {!hideAwaitingContainer && <span>{c.awaitingContainer}</span>}
                      <span>{c.depotCount}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {viewLevel === 'depot' && (
              <div className="map-details">
                <h4>Depots Overview</h4>
                <div className="map-details-header">
                  <span>Depot</span>
                  {!hideDepotLocationColumns && <span>Company</span>}
                  {!hideDepotLocationColumns && <span>Country</span>}
                  <span>Total</span>
                  <span>Available</span>
                  <span>In Use</span>
                  {!hideAwaitingContainer && <span>Awaiting Container</span>}
                </div>
                <div className="map-details-rows">
                  {filteredDepots.map((d) => (
                    <div key={`${d.country}-${d.company}-${d.name}`} className="map-details-row">
                      <span>{d.name}</span>
                      {!hideDepotLocationColumns && <span>{d.company}</span>}
                      {!hideDepotLocationColumns && <span>{d.country}</span>}
                      <span>{d.total}</span>
                      <span>{d.available}</span>
                      <span>{d.inUse}</span>
                      {!hideAwaitingContainer && <span>{d.awaitingContainer}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
